// =============================================================================
// STATS SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { StatsComponent } from '@/types/components';

interface StatsSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new StatsComponent format
 */
function isNewStatsFormat(component: ContentComponent): boolean {
  return (
    component.type === 'stats' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Stats Section Component
 */
export const StatsSection: React.FC<StatsSectionProps> = ({ component }) => {
  if (isNewStatsFormat(component)) {
    return <StatsSectionNew component={component as unknown as any} />;
  }
  return <StatsSectionLegacy component={component} />;
};

/**
 * New format Stats Section
 */
const StatsSectionNew: React.FC<{ component: StatsComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section stats-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className={`grid grid-cols-${layout.columns || 4}`}>
          {items.map((stat, index) => (
            <div key={stat.id || index} className="stat-item">
              <div className="stat-number">
                {stat.prefix && <span className="stat-prefix">{stat.prefix}</span>}
                {stat.number}
                {stat.suffix && <span className="stat-suffix">{stat.suffix}</span>}
              </div>
              <div className="stat-label">{stat.label}</div>
              {stat.description && (
                <div className="stat-description">{stat.description}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Stats Section
 */
const StatsSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const stats = metadata?.stats || [];
  const columns = metadata?.columns || 4;
  
  return (
    <section id={id} className="content-section stats-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`grid grid-cols-${columns}`}>
          {stats.map((stat: any, index: number) => (
            <div key={index} className="stat-item">
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
              {stat.description && (
                <div className="stat-description">{stat.description}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { StatsSectionNew, StatsSectionLegacy };

